import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Image,
} from '@chakra-ui/react'
import QRCode from 'qrcode'
import React, { useCallback, useEffect, useState, useRef } from 'react'
import { FiAward, FiDownload, FiPrinter } from 'react-icons/fi'
import { urlDoCracha } from '../api/lookup'
import { Pessoa } from '../pages/credenciamento/types'
import api from '../utils/api'

// ─── helpers ────────────────────────────────────────────────────

async function loadImageAsBase64(src: string): Promise<string> {
  const res = await fetch(src)
  const blob = await res.blob()
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function getImageSize(src: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight })
    img.onerror = reject
    img.src = src
  })
}

function getCategorySlug(pessoa: Pessoa): string {
  return (
    pessoa.customFields?.categoria ||
    pessoa.categoria ||
    ''
  ).toLowerCase().replace(/\s+/g, '-')
}

function getFooterImage(slug: string): string | null {
  switch (slug) {
    case 'autoridades':
      return '/credentials/rodape_cred_azul_autoridades.jpg'
    case 'expositor':
      return '/credentials/rodape_cred_verde_expositor.jpg'
    case 'imprensa':
      return '/credentials/rodape_cred_imprensa.jpg'
    case 'staff':
    case 'visitantes':
      return null // white footer — rendered programmatically
    case 'agente-de-viagem':
    case 'guia-de-turismo':
    default:
      return '/credentials/rodape_cred_guia_agente.jpg'
  }
}

// ─── PDF builder (A4 with A6 credential at top-left) ────────────

async function buildCredentialPdf(
  pessoa: Pessoa,
  qrCodeDataUrl: string,
): Promise<Blob> {
  const { default: jsPDF } = await import('jspdf')

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })

  // A4 = 210 × 297 mm | Credential = A6 = 105 × 148.5 mm (top-left)
  const credW = 105
  const credH = 148.5
  const credX = 0
  const credY = 0

  // White background
  doc.setFillColor(255, 255, 255)
  doc.rect(credX, credY, credW, credH, 'F')

  const slug = getCategorySlug(pessoa)
  const footerSrc = getFooterImage(slug)
  const hasFooterImage = footerSrc !== null

  const imagePromises: Promise<any>[] = [
    loadImageAsBase64('/credentials/cabecalho.jpg'),
    getImageSize('/credentials/cabecalho.jpg'),
  ]
  if (hasFooterImage) {
    imagePromises.push(loadImageAsBase64(footerSrc))
    imagePromises.push(getImageSize(footerSrc))
  }

  const results = await Promise.all(imagePromises)
  const headerB64 = results[0] as string
  const headerSize = results[1] as { w: number; h: number }
  const footerB64 = hasFooterImage ? (results[2] as string) : ''
  const footerSize = hasFooterImage ? (results[3] as { w: number; h: number }) : { w: credW, h: 0 }

  const headerH = (credW * headerSize.h) / headerSize.w
  const footerH = hasFooterImage ? (credW * footerSize.h) / footerSize.w : 15

  // Header
  doc.addImage(headerB64, 'JPEG', credX, credY, credW, headerH)

  // Footer
  if (hasFooterImage) {
    doc.addImage(footerB64, 'JPEG', credX, credY + credH - footerH, credW, footerH)
  } else {
    // White footer with subtle border line
    doc.setFillColor(255, 255, 255)
    doc.rect(credX, credY + credH - footerH, credW, footerH, 'F')
    doc.setDrawColor(200, 200, 200)
    doc.setLineWidth(0.3)
    doc.setLineDashPattern([], 0)
    doc.line(credX + 5, credY + credH - footerH, credX + credW - 5, credY + credH - footerH)
  }

  // Middle section
  const midTop = credY + headerH
  const midBot = credY + credH - footerH
  const midH = midBot - midTop
  const cx = credX + credW / 2

  // Category label
  const isGuide = slug === 'agente-de-viagem' && !!pessoa.customFields?.cadastur
  const categoryLabel = isGuide
    ? 'GUIA DE TURISMO'
    : (pessoa.customFields?.categoriaNome || pessoa.customFields?.categoria || pessoa.categoria || '').toUpperCase()

  let lineY = midTop + midH * 0.08
  const lineGap = 3

  // Category
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.setTextColor(0, 102, 179)
  const catLines = doc.splitTextToSize(categoryLabel, credW - 10)
  doc.text(catLines, cx, lineY, { align: 'center' })
  lineY += catLines.length * 3.5 + lineGap

  // Name
  const nomeCracha = (pessoa.customFields?.nomeNoCracha || pessoa.nome || '').toUpperCase()
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(15, 15, 15)
  const nameLines = doc.splitTextToSize(nomeCracha, credW - 10)
  doc.text(nameLines, cx, lineY, { align: 'center' })
  lineY += nameLines.length * 4.8 + lineGap

  // Organization / Company
  const empresa = (pessoa.customFields?.empresa || '').toUpperCase()
  if (empresa) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8.5)
    doc.setTextColor(50, 50, 50)
    const empLines = doc.splitTextToSize(empresa, credW - 10)
    doc.text(empLines, cx, lineY, { align: 'center' })
    lineY += empLines.length * 3.8 + lineGap * 0.7
  }

  // Cargo (if available)
  const cargo = (pessoa.customFields?.cargo || pessoa.customFields?.cargoNaEmpresa || '').toUpperCase()
  if (cargo) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(90, 90, 90)
    const cargoLines = doc.splitTextToSize(cargo, credW - 10)
    doc.text(cargoLines, cx, lineY, { align: 'center' })
    lineY += cargoLines.length * 3.5 + lineGap
  }

  // QR Code
  const qrSize = 32
  const qrX = cx - qrSize / 2
  const codeRowH = pessoa.registrationUid ? 6 : 0
  const qrY = Math.min(lineY + 2, midBot - qrSize - codeRowH - 2)

  if (qrCodeDataUrl) {
    doc.addImage(qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize)
  }

  // Registration UID below QR
  if (pessoa.registrationUid) {
    doc.setFont('courier', 'bold')
    doc.setFontSize(7)
    doc.setTextColor(130, 130, 130)
    doc.text(pessoa.registrationUid, cx, qrY + qrSize + 4, { align: 'center' })
  }

  // Dashed cut border
  doc.setDrawColor(180, 180, 180)
  doc.setLineWidth(0.2)
  doc.setLineDashPattern([1, 1], 0)
  doc.rect(credX, credY, credW, credH)

  return doc.output('blob')
}

// ─── Component ──────────────────────────────────────────────────

interface CredentialModalProps {
  isOpen: boolean
  onClose: () => void
  pessoa: Pessoa | null
}

const CredentialModal: React.FC<CredentialModalProps> = ({ isOpen, onClose, pessoa }) => {
  const [generating, setGenerating] = useState(false)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('')
  const [aguardandoConfirmacao, setAguardandoConfirmacao] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  useEffect(() => {
    if (pessoa?.registrationUid) {
      QRCode.toDataURL(pessoa.registrationUid, {
        width: 300,
        margin: 2,
        errorCorrectionLevel: 'H',
        color: { dark: '#000000', light: '#FFFFFF' },
      }).then(setQrCodeDataUrl).catch(console.error)
    }
  }, [pessoa?.registrationUid])

  const slug = pessoa ? getCategorySlug(pessoa) : ''
  const isGuide = slug === 'agente-de-viagem' && !!pessoa?.customFields?.cadastur
  const categoryLabel = isGuide
    ? 'GUIA DE TURISMO'
    : (pessoa?.customFields?.categoriaNome || pessoa?.customFields?.categoria || pessoa?.categoria || '').toUpperCase()
  const nomeCracha = (pessoa?.customFields?.nomeNoCracha || pessoa?.nome || '').toUpperCase()
  const empresa = (pessoa?.customFields?.empresa || '').toUpperCase()
  const cargo = (pessoa?.customFields?.cargo || pessoa?.customFields?.cargoNaEmpresa || '').toUpperCase()

  /**
   * Imprime a etiqueta usando o modelo do evento, renderizado pelo servidor.
   *
   * O balcão e o totem passam a emitir o MESMO crachá: mesmo tamanho físico, mesmos
   * campos, mesmo QR. Antes cada tela montava o seu — o balcão em PDF pelo jsPDF, o
   * totem em HTML — e os dois divergiam do que o briefing pedia.
   *
   * O HTML é buscado e injetado por `srcdoc` em vez de apontado por `src`: a folha vem
   * de outro domínio, e um iframe cross-origin faria `print()` lançar SecurityError.
   * Funciona porque a folha é autossuficiente — CSS inline e QR como SVG embutido.
   *
   * Se o evento não tiver modelo configurado, cai no PDF antigo.
   */
  const handlePrint = useCallback(async () => {
    if (!pessoa) return
    setGenerating(true)
    try {
      const codigo = pessoa.credentialCode || pessoa.registrationUid
      if (codigo) {
        try {
          const resp = await fetch(urlDoCracha(codigo))
          if (resp.ok) {
            const html = await resp.text()
            const iframe = iframeRef.current
            if (iframe) {
              await new Promise<void>(resolve => {
                iframe.onload = () => resolve()
                iframe.srcdoc = html
              })
              iframe.contentWindow?.focus()
              iframe.contentWindow?.print()
              setAguardandoConfirmacao(true)
              return
            }
          }
        } catch (err) {
          console.warn('Modelo do evento indisponível, usando o PDF.', err)
        }
      }

      const blob = await buildCredentialPdf(pessoa, qrCodeDataUrl)
      const url = URL.createObjectURL(blob)
      const printWindow = window.open(url, '_blank')
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          setTimeout(() => printWindow.print(), 500)
        })
      }
      setAguardandoConfirmacao(true)
    } catch (err: any) {
      console.error('Erro ao gerar credencial:', err)
    } finally {
      setGenerating(false)
    }
  }, [pessoa, qrCodeDataUrl])

  /**
   * Só a confirmação de que a etiqueta saiu registra o check-in.
   *
   * O evento de impressão do navegador retorna igual com a impressora sem etiqueta ou
   * com o diálogo cancelado — gravar ali marcava presente quem não recebeu nada.
   */
  const confirmarEntrega = useCallback(async () => {
    const reg = pessoa?.registrationUid
    setAguardandoConfirmacao(false)
    if (!reg) return
    try {
      await api.post('/checkin', { registration: reg, room: null })
    } catch {
      // A credencial já está com a pessoa; o check-in é reconciliável depois. Travar a
      // fila do balcão por causa do registro seria pior que o registro faltante.
    }
    onClose()
  }, [pessoa, onClose])

  const handleDownload = useCallback(async () => {
    if (!pessoa) return
    setGenerating(true)
    try {
      const blob = await buildCredentialPdf(pessoa, qrCodeDataUrl)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `credencial-${pessoa.registrationUid || pessoa.id || 'sem-uid'}.pdf`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    } catch (err: any) {
      console.error('Erro ao baixar PDF:', err)
    } finally {
      setGenerating(false)
    }
  }, [pessoa, qrCodeDataUrl])

  if (!pessoa) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap={2}>
            <FiAward color="#0066b3" />
            <Text>Credencial</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={4}>
          <Flex direction="column" align="center">
            {/* Preview box — proportional to A6 (105 × 148.5 mm) */}
            <Box
              w="280px"
              bg="white"
              border="1px dashed"
              borderColor="gray.300"
              borderRadius="md"
              overflow="hidden"
              boxShadow="lg"
              fontFamily="Arial, Helvetica, sans-serif"
            >
              {/* Header */}
              <Image
                src="/credentials/cabecalho.jpg"
                alt="Cabeçalho"
                w="100%"
                display="block"
              />

              {/* Middle content */}
              <Flex
                direction="column"
                align="center"
                textAlign="center"
                px="10px"
                py="14px"
                gap="5px"
                bg="white"
              >
                {/* Category */}
                <Text
                  fontSize="9px"
                  fontWeight="700"
                  color="#0066b3"
                  letterSpacing="0.5px"
                  textTransform="uppercase"
                >
                  {categoryLabel || '—'}
                </Text>

                {/* Name */}
                <Text
                  fontSize="14px"
                  fontWeight="700"
                  color="#111"
                  lineHeight="1.25"
                  letterSpacing="0.4px"
                  wordBreak="break-word"
                >
                  {nomeCracha || '—'}
                </Text>

                {/* Company */}
                {empresa && (
                  <Text fontSize="10px" color="#333" fontWeight={500} wordBreak="break-word">
                    {empresa}
                  </Text>
                )}

                {/* Cargo */}
                {cargo && (
                  <Text fontSize="9px" color="#666" wordBreak="break-word">
                    {cargo}
                  </Text>
                )}

                {/* QR Code */}
                {qrCodeDataUrl ? (
                  <Image
                    src={qrCodeDataUrl}
                    alt="QR Code"
                    w="90px"
                    h="90px"
                    mt="4px"
                  />
                ) : (
                  <Box
                    w="90px"
                    h="90px"
                    bg="gray.100"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="10px"
                    color="gray.400"
                    border="1px solid"
                    borderColor="gray.200"
                    mt="4px"
                  >
                    Sem QR Code
                  </Box>
                )}

                {/* Registration UID */}
                {pessoa.registrationUid && (
                  <Text
                    fontSize="8px"
                    color="#999"
                    fontFamily="monospace"
                    letterSpacing="1.2px"
                  >
                    {pessoa.registrationUid}
                  </Text>
                )}
              </Flex>

              {/* Footer */}
              {getFooterImage(slug) ? (
                <Image
                  src={getFooterImage(slug)!}
                  alt="Rodapé"
                  w="100%"
                  display="block"
                />
              ) : (
                <Box
                  w="100%"
                  h="30px"
                  bg="white"
                  borderTop="1px solid"
                  borderColor="gray.200"
                />
              )}
            </Box>

            <Text mt={2} fontSize="xs" color="gray.500" textAlign="center">
              PDF A4 · credencial no canto superior esquerdo em tamanho A6 (¼ da folha)
            </Text>
            {pessoa.email && (
              <Text mt={0.5} fontSize="xs" color="gray.500" textAlign="center" noOfLines={1}>
                {pessoa.email}
              </Text>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter gap={3} flexDirection="column">
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            Credencial não disponível para esse evento
          </Alert>
          {/* Folha de impressão: mesma origem via srcdoc, para `print()` ser permitido. */}
          <iframe
            ref={iframeRef}
            title="cracha"
            style={{ position: 'fixed', width: 0, height: 0, border: 0, visibility: 'hidden' }}
          />

          {aguardandoConfirmacao ? (
            /*
              Confirmação de entrega.

              O evento de impressão do navegador retorna igual com a impressora sem
              etiqueta ou com o diálogo cancelado. Gravar o check-in ali marcava presente
              quem não recebeu nada — e o participante só descobria na porta da sala.
            */
            <Flex direction="column" gap={3} w="100%">
              <Text fontWeight="600" textAlign="center">
                A etiqueta saiu corretamente?
              </Text>
              <Flex gap={3} w="100%">
                <Button
                  colorScheme="gray"
                  variant="outline"
                  onClick={handlePrint}
                  isLoading={generating}
                  flex={1}
                >
                  Não saiu — imprimir de novo
                </Button>
                <Button colorScheme="green" onClick={confirmarEntrega} flex={1}>
                  Sim, entregue
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Flex gap={3} w="100%">
              <Button
                leftIcon={generating ? <Spinner size="sm" /> : <FiDownload />}
                colorScheme="blue"
                variant="outline"
                onClick={handleDownload}
                flex={1}
              >
                Baixar PDF
              </Button>
              <Button
                leftIcon={generating ? <Spinner size="sm" /> : <FiPrinter />}
                colorScheme="blue"
                onClick={handlePrint}
                isLoading={generating}
                flex={1}
              >
                Imprimir etiqueta
              </Button>
            </Flex>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default CredentialModal
